import { fmtINR } from "@/lib/documents";
import { fmtLong } from "./timelineUtils";

const Dark = ({ children, testId }) => (
  <div className="rounded-lg px-4 py-3 text-white shadow-[0_8px_24px_rgba(0,0,0,0.2)] min-w-[220px] max-w-[280px]" style={{ background: "#021F21" }} data-testid={testId}>
    {children}
    <span className="absolute left-1/2 -translate-x-1/2 -bottom-[6px] w-0 h-0 border-l-[6px] border-r-[6px] border-t-[6px] border-l-transparent border-r-transparent border-t-[#021F21]" />
  </div>
);
const Div = ({ light }) => <div className={`my-2 h-px ${light ? "bg-[#E6EFF0]" : "bg-white/10"}`} />;
const Link = ({ onClick, children, color = "#37F4B5", testId }) => (
  <button type="button" onClick={onClick} className="text-[11px] font-bold hover:underline" style={{ color }} data-testid={testId}>{children}</button>
);

/** Positioned tooltip for a hovered bar / cell. `tip` = { kind, room, seg?, date?, x, y }. */
export function TimelineTooltip({ tip, onAction }) {
  if (!tip) return null;
  const { kind, room, seg, date, x, y } = tip;
  const style = { position: "fixed", left: x, top: y - 10, transform: "translate(-50%, -100%)", zIndex: 60, pointerEvents: "auto" };
  const act = (a) => (e) => { e.stopPropagation(); onAction(a, tip); };
  const dates = seg ? `${fmtLong(seg.check_in || seg.date_from)} → ${fmtLong(seg.check_out || seg.date_to)}` : "";

  if (kind === "day") {
    return (
      <div style={style} className="relative" data-testid="tooltip-day">
        <div className="rounded-lg bg-white border border-[#D5E6E8] shadow-[0_4px_12px_rgba(0,0,0,0.1)] px-4 py-3 min-w-[200px]">
          <div className="text-[13px] font-bold text-[#1A2425]">{fmtLong(seg.date)}</div>
          <div className="text-[11px] text-[#3F5257]">{seg.available} room{seg.available === 1 ? "" : "s"} available{seg.rate_from ? ` · from ${fmtINR(seg.rate_from)}` : ""}</div>
          <Div light />
          {seg.by_type?.length ? seg.by_type.map((t) => (
            <div key={t.room_type} className="flex justify-between gap-4 text-[11px] py-0.5"><span className="text-[#3F5257]">{t.room_type}</span><span className="text-[#1A2425] font-semibold">{t.available} · {t.rate ? fmtINR(t.rate) : "—"}</span></div>
          )) : <div className="text-[11px] text-[#DC2626] font-semibold">Fully booked</div>}
        </div>
      </div>
    );
  }
  if (kind === "vacant") {
    return (
      <div style={style} className="relative" data-testid="tooltip-vacant" onMouseDown={(e) => e.stopPropagation()}>
        <div className="rounded-lg bg-white border border-[#D5E6E8] shadow-[0_4px_12px_rgba(0,0,0,0.1)] px-4 py-3 min-w-[200px]">
          <div className="text-[13px] font-bold text-[#1A2425]">Room {room.room_number}</div>
          <div className="text-[11px] text-[#3F5257]">{room.room_type}</div>
          <Div light />
          <div className="text-[11px] font-semibold text-[#14C48E] inline-flex items-center gap-1.5"><span className="w-1.5 h-1.5 rounded-full bg-[#14C48E]" />Available</div>
          <div className="text-[11px] text-[#97A6A9]">{fmtLong(date)}</div>
          <Div light />
          <div className="flex items-center gap-3">
            <Link onClick={act("walk_in")} color="#14C48E" testId="tooltip-walkin">+ New Walk-In</Link>
            <Link onClick={act("new_reservation")} color="#014751" testId="tooltip-reserve">+ New Reservation</Link>
          </div>
        </div>
      </div>
    );
  }
  return (
    <div style={style} className="relative" onMouseDown={(e) => e.stopPropagation()}>
      {kind === "occupied" && (
        <Dark testId="tooltip-occupied">
          <div className="text-[13px] font-bold">{seg.guest_name || "Guest"}</div>
          <div className="text-[11px] text-white/60">Room {room.room_number} · {room.room_type}</div>
          <Div />
          <div className="text-[11px] text-[#E6FEFF]">{dates}</div>
          <div className="text-[11px] text-[#97A6A9]">{seg.nights} night{seg.nights === 1 ? "" : "s"} · {seg.pax_adults ?? 0} Adult{seg.pax_adults === 1 ? "" : "s"}{seg.pax_children ? `, ${seg.pax_children} Child${seg.pax_children === 1 ? "" : "ren"}` : ""}</div>
          <Div />
          <div className="text-[11px] text-[#97A6A9]">{seg.rate_plan || "Agreed rate"}{seg.rate ? ` · ${fmtINR(seg.rate)}/night` : ""}</div>
          <div className="text-[11px] font-semibold" style={{ color: seg.balance_due > 0.009 ? "#F59E0B" : "#14C48E" }} data-testid="tooltip-balance">{seg.balance_due > 0.009 ? `Balance due ${fmtINR(seg.balance_due)}` : "Paid in full"}</div>
          <Div />
          <div className="flex items-center gap-3"><Link onClick={act("view_stay")} testId="tooltip-view-stay">View Stay →</Link>{seg.stay_status === "checked_in" && <Link onClick={act("check_out")} testId="tooltip-checkout">Check Out →</Link>}</div>
        </Dark>
      )}
      {kind === "reserved" && (
        <Dark testId="tooltip-reserved">
          <div className="text-[13px] font-bold">{seg.guest_name || "Upcoming Reservation"}</div>
          <div className="text-[11px] text-white/60">Room {room.room_number} · {room.room_type}{seg.reservation_code ? ` · ${seg.reservation_code}` : ""}</div>
          <Div />
          <div className="text-[11px] text-[#E6FEFF]">{dates}</div>
          <div className="text-[11px] text-[#97A6A9]">{seg.rate_plan || "Agreed rate"}{seg.rate ? ` · ${fmtINR(seg.rate)}/night` : ""}</div>
          <Div />
          <div className="flex items-center gap-3"><Link onClick={act("view_reservation")} testId="tooltip-view-reservation">View Reservation →</Link><Link onClick={act("cancel_reservation")} color="#F59E0B" testId="tooltip-cancel-reservation">Cancel →</Link></div>
        </Dark>
      )}
      {(kind === "blocked" || kind === "out_of_order") && (
        <Dark testId={`tooltip-${kind}`}>
          <div className="text-[13px] font-bold">Room {room.room_number} — {kind === "blocked" ? "Blocked" : "Out of Order"}</div>
          {seg.reason && <div className="text-[11px] text-[#E6FEFF] mt-1">{seg.reason}{seg.reason_note ? ` · ${seg.reason_note}` : ""}</div>}
          <div className="text-[11px] text-[#97A6A9]">{fmtLong(seg.date_from)} → {fmtLong(seg.date_to)}</div>
          <Div />
          <Link onClick={act(kind === "blocked" ? "unblock" : "mark_available")} testId="tooltip-unblock">{kind === "blocked" ? "Unblock Room" : "Back in Service"}</Link>
        </Dark>
      )}
      {kind === "vacant_dirty" && (
        <Dark testId="tooltip-dirty">
          <div className="text-[13px] font-bold">Room {room.room_number} — Vacant / Dirty</div>
          <div className="text-[11px] text-[#97A6A9]">{fmtLong(seg.date_from)}</div>
          <Div />
          <Link onClick={act("request_housekeeping")} testId="tooltip-hk">Request Housekeeping</Link>
        </Dark>
      )}
    </div>
  );
}
